import { useState } from 'react';
import { toast } from 'react-toastify';
import { MdInventory2, MdAdd, MdDelete, MdCheckCircle, MdEditNote, MdInfoOutline, MdVerified, MdGroups, MdDescription, MdAccountBalance, MdReceiptLong } from 'react-icons/md';
import { FaHandHoldingUsd, FaFileContract } from 'react-icons/fa';
import ThreeColumnLayout from '../../layouts/ThreeColumnLayout';
import FilterSidebar from '../../organisms/FilterSidebar';
import BadgeStatus from '../../atoms/BadgeStatus';
import Tab, { TabProps } from '../../molecules/Tab';
import TextEdit from '../../molecules/TextEdit';
import TextArea from '../../molecules/TextArea';
import SelectTextField from '../../molecules/SelectTextField';
import CurrencyInput from '../../molecules/CurrencyInput';
import NumberEdit from '../../molecules/NumberEdit';
import {
    bankingProducts as initialProducts,
    BankingProduct,
    ResultingAssetType,
    ProductStatus,
    RatingGrade,
    RATING_ORDER,
    ParticipantTypeId,
    PARTICIPANT_TYPES,
    GuaranteeType,
    GUARANTEE_TYPES,
    InstrumentType,
    INSTRUMENT_TYPES,
    DiscountedReceivableType,
    DISCOUNTED_RECEIVABLE_TYPES,
    TernaryFlag,
    TERNARY_OPTIONS,
    InvoiceGranularity,
    INVOICE_GRANULARITY_OPTIONS,
    OperationStructure,
    RegistrationType,
    REGISTRATION_TYPES,
    INSTRUMENT_TO_REGISTRATION_TYPES,
    B3CustodyConfig,
    DimensaRegistrationType,
    DIMENSA_REGISTRATION_TYPES,
    dimensaRegistrationProfiles as initialDimensaProfiles,
    DimensaRegistrationProfile,
    DealStage,
    DEAL_STAGES,
    legalInstruments,
    getInstrumentName,
    RequiredDocumentType,
    REQUIRED_DOCUMENT_TYPES,
    getParticipantTypeLabel,
    getGuaranteeTypeLabel,
    getRatingRangeLabel,
    createEmptyBankingProduct,
} from './productCatalogMockData';

const ASSET_TYPE_OPTIONS: { value: ResultingAssetType; label: string }[] = [
    { value: 'RECEIVABLE_ASSIGNMENT', label: 'Cessão de Recebíveis' },
    { value: 'LOAN', label: 'Empréstimo (CPR-F)' },
];

const STATUS_OPTIONS: { value: ProductStatus; label: string }[] = [
    { value: 'DRAFT', label: 'Em edição (Draft)' },
    { value: 'ACTIVE', label: 'Ativo' },
    { value: 'INACTIVE', label: 'Desativado' },
];

const CURRENCY_OPTIONS = [
    { value: 'BRL', label: 'Real (R$)' },
    { value: 'USD', label: 'Dólar (US$)' },
];

const RATING_OPTIONS = RATING_ORDER.map(r => ({ value: r, label: r }));

const statusColor = (status: ProductStatus): 'green' | 'gray' | 'orange' => {
    if (status === 'ACTIVE') return 'green';
    if (status === 'INACTIVE') return 'gray';
    return 'orange';
};

const statusLabel = (status: ProductStatus): string =>
    STATUS_OPTIONS.find(s => s.value === status)?.label || status;

const formatCurrency = (value: number, currency: 'BRL' | 'USD') =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency }).format(value);

// Ícone e cor por ativo resultante — identidade visual no estilo da apresentação Agrolend.
const ProductIcon = ({ assetType, size = 26 }: { assetType: ResultingAssetType; size?: number }) => {
    if (assetType === 'LOAN') return <FaFileContract size={size} className="text-emerald-600" />;
    return <FaHandHoldingUsd size={size} className="text-emerald-600" />;
};

// Seção do painel — controla qual bloco do formulário aparece.
type FormSection = 'BASIC' | 'ELIGIBILITY' | 'PARTICIPANTS' | 'INSTRUMENTS' | 'B3' | 'DIMENSA';

const FORM_SECTIONS: { value: FormSection; label: string; icon: typeof MdInfoOutline }[] = [
    { value: 'BASIC', label: 'Dados', icon: MdInfoOutline },
    { value: 'ELIGIBILITY', label: 'Elegibilidade', icon: MdVerified },
    { value: 'PARTICIPANTS', label: 'Participantes', icon: MdGroups },
    { value: 'INSTRUMENTS', label: 'Instrumentos', icon: MdDescription },
    { value: 'B3', label: 'Registro B3', icon: MdAccountBalance },
    { value: 'DIMENSA', label: 'ERP-Dimensa', icon: MdReceiptLong },
];

const ProductCatalogPageForm = () => {
    const [products, setProducts] = useState<BankingProduct[]>(initialProducts);
    const [dimensaProfiles, setDimensaProfiles] = useState<DimensaRegistrationProfile[]>(initialDimensaProfiles);
    const [selectedProduct, setSelectedProduct] = useState<BankingProduct | null>(null);
    const [editing, setEditing] = useState<BankingProduct | null>(null);
    const [section, setSection] = useState<FormSection>('BASIC');
    const [statusFilter, setStatusFilter] = useState<ProductStatus | ''>('');
    const [assetFilter, setAssetFilter] = useState<ResultingAssetType | ''>('');

    // Config B3 é 1:1 com o produto (embutida em editing.b3Config). b3Editing controla
    // se a config está em modo edição inline. Não há catálogo de perfis reutilizáveis.
    const [b3Editing, setB3Editing] = useState<boolean>(false);

    // Dimensa ainda segue o padrão antigo de perfil reutilizável (rodada futura).
    const [dimensaMode, setDimensaMode] = useState<'SELECT' | 'EDIT' | 'NEW'>('SELECT');
    const [dimensaDraft, setDimensaDraft] = useState<DimensaRegistrationProfile | null>(null);

    const getDimensaProfileName = (profileId: string | null): string =>
        dimensaProfiles.find(p => p.profileId === profileId)?.profileName || 'Não se aplica';

    const isEditingNew = editing && !products.find(p => p.productId === editing.productId);

    const openNew = () => {
        setEditing(createEmptyBankingProduct());
        setSelectedProduct(null);
        setSection('BASIC');
        setB3Editing(false);
        setDimensaMode('SELECT');
        setDimensaDraft(null);
    };

    const openEdit = (product: BankingProduct) => {
        setEditing(JSON.parse(JSON.stringify(product)));
        setSelectedProduct(null);
        setSection('BASIC');
        setB3Editing(false);
        setDimensaMode('SELECT');
        setDimensaDraft(null);
    };

    const closeForm = () => {
        setEditing(null);
        setB3Editing(false);
        setDimensaMode('SELECT'); setDimensaDraft(null);
    };

    const handleSave = () => {
        if (!editing) return;
        if (!editing.commercialName.trim()) {
            toast.error('Nome comercial é obrigatório');
            return;
        }
        setProducts(prev => {
            const exists = prev.some(p => p.productId === editing.productId);
            return exists
                ? prev.map(p => (p.productId === editing.productId ? editing : p))
                : [editing, ...prev];
        });
        toast.success(`Produto ${editing.commercialName} salvo (mock local — nenhuma API foi chamada)`);
        setEditing(null);
    };

    const updateEditing = (patch: Partial<BankingProduct>) => {
        if (!editing) return;
        setEditing({ ...editing, ...patch });
    };

    const updateOperationStructure = (patch: Partial<OperationStructure>) => {
        if (!editing || !editing.operationStructure) return;
        setEditing({ ...editing, operationStructure: { ...editing.operationStructure, ...patch } });
    };

    // ---- Elegibilidade ----
    const addEligibility = () => {
        if (!editing) return;
        updateEditing({
            eligibilityCriteria: [
                ...editing.eligibilityCriteria,
                { criteriaId: `ec-${Date.now()}`, participantTypeId: 3, minRating: null, maxRating: null },
            ],
        });
    };

    const updateEligibility = (id: string, patch: Partial<{ participantTypeId: ParticipantTypeId; minRating: RatingGrade | null; maxRating: RatingGrade | null }>) => {
        if (!editing) return;
        updateEditing({
            eligibilityCriteria: editing.eligibilityCriteria.map(c => (c.criteriaId === id ? { ...c, ...patch } : c)),
        });
    };

    const removeEligibility = (id: string) => {
        if (!editing) return;
        updateEditing({ eligibilityCriteria: editing.eligibilityCriteria.filter(c => c.criteriaId !== id) });
    };

    // ---- Participantes permitidos ----
    const addParticipant = () => {
        if (!editing) return;
        updateEditing({
            allowedParticipants: [
                ...editing.allowedParticipants,
                { allowedParticipantId: `adp-${Date.now()}`, participantTypeId: 3, isRequired: true, requiredDocuments: [] },
            ],
        });
    };

    const updateParticipant = (id: string, patch: Partial<{ participantTypeId: ParticipantTypeId; isRequired: boolean }>) => {
        if (!editing) return;
        updateEditing({
            allowedParticipants: editing.allowedParticipants.map(p => (p.allowedParticipantId === id ? { ...p, ...patch } : p)),
        });
    };

    const removeParticipant = (id: string) => {
        if (!editing) return;
        updateEditing({ allowedParticipants: editing.allowedParticipants.filter(p => p.allowedParticipantId !== id) });
    };

    const toggleRequiredDocument = (participantId: string, docType: RequiredDocumentType) => {
        if (!editing) return;
        updateEditing({
            allowedParticipants: editing.allowedParticipants.map(p => {
                if (p.allowedParticipantId !== participantId) return p;
                const existing = p.requiredDocuments.find(d => d.documentType === docType);
                const requiredDocuments = existing
                    ? p.requiredDocuments.filter(d => d.documentType !== docType)
                    : [...p.requiredDocuments, { documentType: docType, isRequired: true }];
                return { ...p, requiredDocuments };
            }),
        });
    };

    // ---- Template bindings ----
    const addInstrumentBinding = () => {
        if (!editing) return;
        updateEditing({
            instrumentBindings: [
                ...editing.instrumentBindings,
                { bindingId: `ib-${Date.now()}`, stage: 'CONTRACT_GENERATION', instrumentCode: '' },
            ],
        });
    };

    const updateInstrumentBinding = (id: string, patch: Partial<{ stage: DealStage; instrumentCode: string; customerId?: string }>) => {
        if (!editing) return;
        updateEditing({
            instrumentBindings: editing.instrumentBindings.map(t => (t.bindingId === id ? { ...t, ...patch } : t)),
        });
    };

    const removeInstrumentBinding = (id: string) => {
        if (!editing) return;
        updateEditing({ instrumentBindings: editing.instrumentBindings.filter(t => t.bindingId !== id) });
    };

    // ---- Garantias ----
    const addGuarantee = () => {
        if (!editing) return;
        updateEditing({ guarantees: [...editing.guarantees, { type: 'CESSAO_FIDUCIARIA', requirement: 'MANDATORY' }] });
    };

    const updateGuarantee = (index: number, patch: Partial<{ type: GuaranteeType; requirement: 'MANDATORY' | 'OPTIONAL'; retentionPercentage?: number }>) => {
        if (!editing) return;
        updateEditing({ guarantees: editing.guarantees.map((g, i) => (i === index ? { ...g, ...patch } : g)) });
    };

    const removeGuarantee = (index: number) => {
        if (!editing) return;
        updateEditing({ guarantees: editing.guarantees.filter((_, i) => i !== index) });
    };

    // ---- Config de registro B3 (1:1 com o produto, embutida em editing.b3Config) ----
    const emptyB3Config = (b3Product: RegistrationType): B3CustodyConfig => ({
        b3Product,
        registersGuarantee: false,
        collateralTypeCode: '1',
        operationModalityCode: '',
        finalityCode: '',
        ballastTypeCode: '',
    });

    const startB3Create = () => {
        const instrument = editing?.operationStructure?.instrumentType;
        const allowed = instrument ? INSTRUMENT_TO_REGISTRATION_TYPES[instrument] : null;
        const defaultProduct: RegistrationType = allowed && allowed.length ? allowed[0] : 'CPR_INSTRUMENT';
        updateEditing({ b3Config: emptyB3Config(defaultProduct) });
        setB3Editing(true);
    };

    const removeB3Config = () => {
        updateEditing({ b3Config: null });
        setB3Editing(false);
        toast.success('Configuração B3 removida — produto não registra na B3');
    };

    const updateB3Config = (patch: Partial<B3CustodyConfig>) => {
        if (!editing?.b3Config) return;
        updateEditing({ b3Config: { ...editing.b3Config, ...patch } });
    };

    // ---- Perfis de registro ERP/Dimensa (catálogo compartilhado) ----
    const emptyDimensaProfile = (): DimensaRegistrationProfile => ({
        profileId: `dmprof-${Date.now()}`,
        profileName: '',
        registrationType: 'DIMENSA',
        siglaModalidade: '',
        codigoCarteira: '',
        codigoProdutoDimensa: '',
        tipoOperacao: '',
    });

    const startDimensaEdit = () => {
        const current = dimensaProfiles.find(p => p.profileId === editing?.dimensaRegistrationProfileId);
        if (!current) return;
        setDimensaDraft(JSON.parse(JSON.stringify(current)));
        setDimensaMode('EDIT');
    };

    const startDimensaNew = () => {
        setDimensaDraft(emptyDimensaProfile());
        setDimensaMode('NEW');
    };

    const cancelDimensaEdit = () => {
        setDimensaDraft(null);
        setDimensaMode('SELECT');
    };

    const saveDimensaDraft = () => {
        if (!dimensaDraft) return;
        if (!dimensaDraft.profileName.trim()) {
            toast.error('Nome do perfil é obrigatório');
            return;
        }
        setDimensaProfiles(prev => {
            const exists = prev.some(p => p.profileId === dimensaDraft.profileId);
            return exists
                ? prev.map(p => (p.profileId === dimensaDraft.profileId ? dimensaDraft : p))
                : [...prev, dimensaDraft];
        });
        updateEditing({ dimensaRegistrationProfileId: dimensaDraft.profileId });
        toast.success(`Perfil "${dimensaDraft.profileName}" salvo (mock local)`);
        setDimensaDraft(null);
        setDimensaMode('SELECT');
    };

    const updateDimensaDraft = (patch: Partial<DimensaRegistrationProfile>) => {
        if (!dimensaDraft) return;
        setDimensaDraft({ ...dimensaDraft, ...patch });
    };

    const filteredProducts = products.filter(p => {
        if (statusFilter && p.status !== statusFilter) return false;
        if (assetFilter && p.resultingAssetType !== assetFilter) return false;
        return true;
    });

    const statusCounts = STATUS_OPTIONS.reduce((acc, s) => {
        acc[s.value] = products.filter(p => p.status === s.value).length;
        return acc;
    }, {} as Record<string, number>);

    const filterGroups = [
        {
            title: '',
            children: (
                <div className="space-y-4">
                    <button onClick={() => { setStatusFilter(''); setAssetFilter(''); }}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${!statusFilter && !assetFilter
                            ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 font-medium'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700'}`}>
                        <div className="flex items-center justify-between">
                            <span>Todos</span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">{products.length}</span>
                        </div>
                    </button>

                    <div className="border-t border-gray-200 dark:border-slate-700 pt-3">
                        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 px-3">STATUS</div>
                        <div className="space-y-1">
                            {STATUS_OPTIONS.map(s => (
                                <button key={s.value} onClick={() => setStatusFilter(statusFilter === s.value ? '' : s.value)}
                                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${statusFilter === s.value
                                        ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 font-medium'
                                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700'}`}>
                                    <div className="flex items-center justify-between">
                                        <span>{s.label}</span>
                                        <span className="text-xs text-gray-500 dark:text-gray-400">{statusCounts[s.value] || 0}</span>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="border-t border-gray-200 dark:border-slate-700 pt-3">
                        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 px-3">ATIVO RESULTANTE</div>
                        <div className="space-y-1">
                            {ASSET_TYPE_OPTIONS.map(a => (
                                <button key={a.value} onClick={() => setAssetFilter(assetFilter === a.value ? '' : a.value)}
                                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${assetFilter === a.value
                                        ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 font-medium'
                                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700'}`}>
                                    {a.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            ),
        },
    ];

    return (
        <>
            {!editing && (
                <div className={`fixed top-[69px] ${selectedProduct ? 'right-[665px]' : 'right-[10px]'} z-50`}>
                    <button onClick={openNew}
                        className="w-10 h-10 rounded-full shadow-xl hover:shadow-2xl transition-all duration-300 flex items-center justify-center border-2 border-white dark:border-slate-800 bg-blue-600 hover:bg-blue-700 text-white"
                        title="Novo produto">
                        <MdAdd size={18} />
                    </button>
                </div>
            )}

            <ThreeColumnLayout
                leftSidebarTitle={
                    <div className="flex items-center justify-between w-full">
                        <div className="flex items-center space-x-2">
                            <MdInventory2 className="text-blue-600" size={18} />
                            <span>Produtos</span>
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                            <div>v0.1.0</div>
                            <div className="font-medium text-green-600">MOCK</div>
                        </div>
                    </div>
                }
                leftSidebar={<FilterSidebar filterGroups={filterGroups} />}
                rightPanelWidth="w-[680px]"
                closeButtonRight="right-[665px]"
                showRightPanel={!!editing || !!selectedProduct}
                onCloseRightPanel={() => { closeForm(); setSelectedProduct(null); }}
                rightPanel={editing ? (
                    <div className="h-full flex flex-col">
                        <div className="flex-shrink-0 border-b border-gray-200 dark:border-slate-700 p-4">
                            <div className="flex items-center justify-between mb-3">
                                <h2 className="text-lg font-semibold">
                                    {isEditingNew ? 'Novo Produto' : `Editar: ${editing.commercialName}`}
                                </h2>
                                <button onClick={handleSave} className="px-4 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm">
                                    Salvar
                                </button>
                            </div>
                            <div className="-mb-4 overflow-x-auto">
                                <Tab
                                    tabItems={FORM_SECTIONS.map((s, i): TabProps => ({
                                        id: `sec-${s.value}`, title: s.label, index: i,
                                    }))}
                                    tabIndex={FORM_SECTIONS.findIndex(s => s.value === section)}
                                    setTabIndex={(i) => setSection(FORM_SECTIONS[i].value)}
                                    compact
                                />
                            </div>
                        </div>

                        <div className="flex-1 overflow-y-auto p-6 space-y-4">
                            {section === 'BASIC' && (
                                <div className="space-y-4">
                                    <TextEdit label="Nome comercial *" value={editing.commercialName}
                                        onChange={e => updateEditing({ commercialName: e.target.value })} />
                                    <div className="grid grid-cols-3 gap-4">
                                        <div className="col-span-2">
                                            <TextEdit label="Código curto" value={editing.shortCode}
                                                onChange={e => updateEditing({ shortCode: e.target.value })} />
                                        </div>
                                        <TextEdit label="Ícone (emoji)" value={editing.icon || ''}
                                            onChange={e => updateEditing({ icon: e.target.value })} />
                                    </div>
                                    <TextEdit label="Nome por extenso" value={editing.fullName || ''}
                                        onChange={e => updateEditing({ fullName: e.target.value })} />
                                    <TextArea label="Descrição" rows={4} value={editing.description}
                                        onChange={e => updateEditing({ description: e.target.value })} />
                                    <SelectTextField label="Ativo resultante *" items={ASSET_TYPE_OPTIONS}
                                        value={editing.resultingAssetType}
                                        onChange={e => updateEditing({ resultingAssetType: e.target.value as ResultingAssetType })} />
                                    <SelectTextField label="Status" items={STATUS_OPTIONS}
                                        value={editing.status}
                                        onChange={e => updateEditing({ status: e.target.value as ProductStatus })} />
                                    <SelectTextField label="Moeda" items={CURRENCY_OPTIONS}
                                        value={editing.currency}
                                        onChange={e => updateEditing({ currency: e.target.value as 'BRL' | 'USD' })} />
                                    <div className="grid grid-cols-2 gap-4">
                                        <CurrencyInput label="Taxa mínima (% a.m.)" value={editing.minRate} fractionDigits={2}
                                            onChange={value => updateEditing({ minRate: value })} />
                                        <CurrencyInput label="Taxa máxima (% a.m.)" value={editing.maxRate} fractionDigits={2}
                                            onChange={value => updateEditing({ maxRate: value })} />
                                    </div>
                                    <CurrencyInput label={`Valor máximo (${editing.currency === 'BRL' ? 'R$' : 'US$'})`}
                                        prefix={editing.currency === 'BRL' ? 'R$' : 'US$'}
                                        value={editing.maxAmount} fractionDigits={2}
                                        onChange={value => updateEditing({ maxAmount: value })} />
                                    <NumberEdit label="Prazo máximo (dias)" value={editing.maxTerm}
                                        onChange={e => updateEditing({ maxTerm: Number(e.target.value) })} />

                                    {editing.operationStructure && (
                                        <div className="border-t pt-4 space-y-3">
                                            <label className="text-xs text-gray-500 uppercase font-medium">Estrutura da operação (planilha da mesa)</label>
                                            <SelectTextField label="Instrumento" items={INSTRUMENT_TYPES}
                                                value={editing.operationStructure.instrumentType}
                                                onChange={e => updateOperationStructure({ instrumentType: e.target.value as InstrumentType })} />
                                            <div className="grid grid-cols-2 gap-4">
                                                <SelectTextField label="Recebível descontado" items={DISCOUNTED_RECEIVABLE_TYPES}
                                                    value={editing.operationStructure.discountedReceivable}
                                                    onChange={e => updateOperationStructure({ discountedReceivable: e.target.value as DiscountedReceivableType })} />
                                                <SelectTextField label="Regresso do recebível" items={TERNARY_OPTIONS}
                                                    value={editing.operationStructure.receivableRecourse}
                                                    onChange={e => updateOperationStructure({ receivableRecourse: e.target.value as TernaryFlag })} />
                                            </div>
                                            <div className="grid grid-cols-2 gap-4">
                                                <TextEdit label="Garantia obrigatória" value={editing.operationStructure.mandatoryGuaranteeLabel}
                                                    onChange={e => updateOperationStructure({ mandatoryGuaranteeLabel: e.target.value })} />
                                                <SelectTextField label="Regresso da garantia" items={TERNARY_OPTIONS}
                                                    value={editing.operationStructure.guaranteeRecourse}
                                                    onChange={e => updateOperationStructure({ guaranteeRecourse: e.target.value as TernaryFlag })} />
                                            </div>
                                            <SelectTextField label="Fatura" items={INVOICE_GRANULARITY_OPTIONS}
                                                value={editing.operationStructure.invoiceGranularity}
                                                onChange={e => updateOperationStructure({ invoiceGranularity: e.target.value as InvoiceGranularity })} />
                                            <label className="flex items-center gap-2 text-sm">
                                                <input type="checkbox" checked={editing.operationStructure.isLcaBallast}
                                                    onChange={e => updateOperationStructure({ isLcaBallast: e.target.checked })} />
                                                É lastro para LCA
                                            </label>
                                        </div>
                                    )}

                                    <div className="border-t pt-4">
                                        <div className="flex items-center justify-between mb-2">
                                            <label className="text-xs text-gray-500 uppercase font-medium">Garantias</label>
                                            <button onClick={addGuarantee} className="text-xs text-blue-600 hover:underline flex items-center gap-1">
                                                <MdAdd size={14} /> Adicionar
                                            </button>
                                        </div>
                                        <div className="space-y-2">
                                            {editing.guarantees.map((g, i) => (
                                                <div key={i} className="flex items-center gap-2 bg-gray-50 dark:bg-slate-700 p-2 rounded">
                                                    <select value={g.type} onChange={e => updateGuarantee(i, { type: e.target.value as GuaranteeType })}
                                                        className="flex-1 text-sm bg-transparent border-0 border-b border-gray-300 focus:outline-none">
                                                        {GUARANTEE_TYPES.map(gt => <option key={gt.value} value={gt.value}>{gt.label}</option>)}
                                                    </select>
                                                    <select value={g.requirement} onChange={e => updateGuarantee(i, { requirement: e.target.value as 'MANDATORY' | 'OPTIONAL' })}
                                                        className="text-sm bg-transparent border-0 border-b border-gray-300 focus:outline-none">
                                                        <option value="MANDATORY">Obrigatória</option>
                                                        <option value="OPTIONAL">Opcional</option>
                                                    </select>
                                                    {g.type === 'CDB_VINCULADO' && (
                                                        <input type="number" placeholder="% retenção" value={g.retentionPercentage || ''}
                                                            onChange={e => updateGuarantee(i, { retentionPercentage: Number(e.target.value) })}
                                                            className="w-24 text-sm bg-transparent border-0 border-b border-gray-300 focus:outline-none" />
                                                    )}
                                                    <button onClick={() => removeGuarantee(i)} className="text-red-500 hover:text-red-700">
                                                        <MdDelete size={16} />
                                                    </button>
                                                </div>
                                            ))}
                                            {editing.guarantees.length === 0 && (
                                                <p className="text-sm text-gray-400 italic">Nenhuma garantia configurada</p>
                                            )}
                                        </div>
                                        {editing.guarantees.some(g => g.type === 'CDB_VINCULADO') && (
                                            <p className="text-xs text-amber-600 mt-2">
                                                CDB Vinculado: fronteira de bounded context ainda em aberto (collateral vs.
                                                instrumento financeiro à parte) — ver business-context.md do product_catalog.
                                            </p>
                                        )}
                                    </div>
                                </div>
                            )}

                            {section === 'ELIGIBILITY' && (
                                <div className="space-y-4">
                                    <div className="flex items-center justify-between">
                                        <p className="text-sm text-gray-500">
                                            Faixa de rating aceitável por papel (escala Rating 2.0 do CAE — AA a G).
                                            Deixe em branco para "Todos" (sem piso/teto).
                                        </p>
                                        <button onClick={addEligibility} className="text-xs text-blue-600 hover:underline flex items-center gap-1 whitespace-nowrap ml-2">
                                            <MdAdd size={14} /> Adicionar
                                        </button>
                                    </div>
                                    {editing.eligibilityCriteria.map(c => (
                                        <div key={c.criteriaId} className="border border-gray-200 dark:border-slate-600 rounded-lg p-3 space-y-2">
                                            <div className="flex items-center justify-between">
                                                <select value={c.participantTypeId}
                                                    onChange={e => updateEligibility(c.criteriaId, { participantTypeId: Number(e.target.value) as ParticipantTypeId })}
                                                    className="flex-1 text-sm font-medium bg-transparent border-0 border-b border-gray-300 focus:outline-none">
                                                    {PARTICIPANT_TYPES.map(pt => <option key={pt.id} value={pt.id}>{pt.label}</option>)}
                                                </select>
                                                <button onClick={() => removeEligibility(c.criteriaId)} className="text-red-500 hover:text-red-700 ml-2">
                                                    <MdDelete size={16} />
                                                </button>
                                            </div>
                                            <div className="grid grid-cols-2 gap-2">
                                                <select value={c.minRating || ''}
                                                    onChange={e => updateEligibility(c.criteriaId, { minRating: (e.target.value || null) as RatingGrade | null })}
                                                    className="text-sm bg-transparent border-0 border-b border-gray-300 focus:outline-none">
                                                    <option value="">Sem piso (Todos)</option>
                                                    {RATING_OPTIONS.map(r => <option key={r.value} value={r.value}>Min: {r.label}</option>)}
                                                </select>
                                                <select value={c.maxRating || ''}
                                                    onChange={e => updateEligibility(c.criteriaId, { maxRating: (e.target.value || null) as RatingGrade | null })}
                                                    className="text-sm bg-transparent border-0 border-b border-gray-300 focus:outline-none">
                                                    <option value="">Sem teto (Todos)</option>
                                                    {RATING_OPTIONS.map(r => <option key={r.value} value={r.value}>Max: {r.label}</option>)}
                                                </select>
                                            </div>
                                        </div>
                                    ))}
                                    {editing.eligibilityCriteria.length === 0 && (
                                        <p className="text-sm text-gray-400 italic">Nenhum critério de elegibilidade configurado</p>
                                    )}
                                </div>
                            )}

                            {section === 'PARTICIPANTS' && (
                                <div className="space-y-4">
                                    <div className="flex items-center justify-between">
                                        <p className="text-sm text-gray-500">
                                            Tipos de participante permitidos no Deal e documentos exigidos de cada um
                                            (ver decisão #8 do business-context.md — vive no Deal, não no BankingProduct).
                                        </p>
                                        <button onClick={addParticipant} className="text-xs text-blue-600 hover:underline flex items-center gap-1 whitespace-nowrap ml-2">
                                            <MdAdd size={14} /> Adicionar
                                        </button>
                                    </div>
                                    {editing.allowedParticipants.map(p => (
                                        <div key={p.allowedParticipantId} className="border border-gray-200 dark:border-slate-600 rounded-lg p-3 space-y-3">
                                            <div className="flex items-center justify-between">
                                                <select value={p.participantTypeId}
                                                    onChange={e => updateParticipant(p.allowedParticipantId, { participantTypeId: Number(e.target.value) as ParticipantTypeId })}
                                                    className="flex-1 text-sm font-medium bg-transparent border-0 border-b border-gray-300 focus:outline-none">
                                                    {PARTICIPANT_TYPES.map(pt => <option key={pt.id} value={pt.id}>{pt.label}</option>)}
                                                </select>
                                                <label className="flex items-center gap-1 text-xs ml-2 whitespace-nowrap">
                                                    <input type="checkbox" checked={p.isRequired}
                                                        onChange={e => updateParticipant(p.allowedParticipantId, { isRequired: e.target.checked })} />
                                                    Obrigatório
                                                </label>
                                                <button onClick={() => removeParticipant(p.allowedParticipantId)} className="text-red-500 hover:text-red-700 ml-2">
                                                    <MdDelete size={16} />
                                                </button>
                                            </div>
                                            <div>
                                                <div className="text-xs text-gray-500 uppercase mb-1">Documentos exigidos</div>
                                                <div className="flex flex-wrap gap-1.5">
                                                    {REQUIRED_DOCUMENT_TYPES.map(doc => {
                                                        const active = p.requiredDocuments.some(d => d.documentType === doc.value);
                                                        return (
                                                            <button key={doc.value} onClick={() => toggleRequiredDocument(p.allowedParticipantId, doc.value)}
                                                                className={`px-2 py-1 rounded text-xs flex items-center gap-1 transition-colors ${active
                                                                    ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                                                                    : 'bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-gray-300'}`}>
                                                                {active && <MdCheckCircle size={12} />}
                                                                {doc.label}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                    {editing.allowedParticipants.length === 0 && (
                                        <p className="text-sm text-gray-400 italic">Nenhum participante configurado</p>
                                    )}
                                </div>
                            )}

                            {section === 'INSTRUMENTS' && (
                                <div className="space-y-4">
                                    <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-900/10 p-3 text-xs text-gray-600 dark:text-gray-300">
                                        O <b>jurídico</b> é dono dos instrumentos/templates. Aqui o produto apenas
                                        <b> seleciona</b> de um catálogo de instrumentos <b>ativos e liberados</b> — não
                                        cria nem edita. O vínculo é por estágio do Deal; override por cliente segue a
                                        cascata do TemplateBinding (ver ADR-001 do product_catalog).
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <p className="text-sm text-gray-500">Instrumentos por estágio</p>
                                        <button onClick={addInstrumentBinding} className="text-xs text-blue-600 hover:underline flex items-center gap-1 whitespace-nowrap ml-2">
                                            <MdAdd size={14} /> Adicionar
                                        </button>
                                    </div>
                                    {editing.instrumentBindings.map(t => (
                                        <div key={t.bindingId} className="border border-gray-200 dark:border-slate-600 rounded-lg p-3 space-y-2">
                                            <div className="flex items-center justify-between">
                                                <select value={t.stage}
                                                    onChange={e => updateInstrumentBinding(t.bindingId, { stage: e.target.value as DealStage })}
                                                    className="text-sm font-medium bg-transparent border-0 border-b border-gray-300 focus:outline-none">
                                                    {DEAL_STAGES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                                                </select>
                                                <button onClick={() => removeInstrumentBinding(t.bindingId)} className="text-red-500 hover:text-red-700">
                                                    <MdDelete size={16} />
                                                </button>
                                            </div>
                                            <SelectTextField label="Instrumento jurídico (ativos/liberados)"
                                                items={legalInstruments.filter(i => i.status === 'ACTIVE' && i.released).map(i => ({ value: i.instrumentCode, label: i.name }))}
                                                value={t.instrumentCode}
                                                emptyLabel="Selecione um instrumento..."
                                                onChange={e => updateInstrumentBinding(t.bindingId, { instrumentCode: e.target.value })} />
                                            <TextEdit label="Cliente específico (opcional — override por cliente)" value={t.customerId || ''}
                                                onChange={e => updateInstrumentBinding(t.bindingId, { customerId: e.target.value })} />
                                        </div>
                                    ))}
                                    {editing.instrumentBindings.length === 0 && (
                                        <p className="text-sm text-gray-400 italic">Nenhum instrumento vinculado</p>
                                    )}
                                </div>
                            )}

                            {section === 'B3' && (
                                <div className="space-y-4">
                                    <p className="text-sm text-gray-500">
                                        Configuração de registro na B3 (CustodyProduct no b3_custody), 1:1 com este
                                        produto. Se o produto não registra na B3, não há configuração. Ver
                                        b3_custody/docs/decisions/ADR-004.
                                    </p>

                                    <div className="rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50/50 dark:bg-emerald-900/10 p-3 text-xs space-y-2">
                                        <div className="font-semibold text-emerald-800 dark:text-emerald-300 flex items-center gap-1">
                                            <MdInfoOutline size={14} /> O que se configura aqui (ADR-004 do b3_custody)
                                        </div>
                                        <p className="text-gray-600 dark:text-gray-300">
                                            Só o que <b>varia por produto</b>: identidade da Agrolend na B3 (contas OTC,
                                            credor, modalidade/finalidade SCR — Cat.2) e a <b>regra de garantia</b>
                                            (registra? qual tipo? — Cat.3).
                                        </p>
                                        <p className="text-gray-600 dark:text-gray-300">
                                            <b>Não</b> se configura aqui: parâmetros <b>fixos técnicos</b> da B3 (Cat.1,
                                            permanecem no SSM) nem <b>dados da operação</b> (Cat.4: valor, datas,
                                            documento do garantidor — vêm no evento CREDIT_OPERATION_BILLED; se faltarem,
                                            o b3_custody emite CUSTODY_OPERATION_VALIDATION_ERROR).
                                        </p>
                                    </div>

                                    {!editing.b3Config && !b3Editing && (
                                        <div className="rounded-lg border border-dashed border-gray-300 dark:border-slate-600 p-6 text-center space-y-3">
                                            <p className="text-sm text-gray-500">Produto de crédito não registrável na B3.</p>
                                            <button onClick={startB3Create}
                                                className="inline-flex items-center gap-1 px-4 py-2 text-sm rounded bg-blue-600 text-white hover:bg-blue-700">
                                                <MdAdd size={16} /> Criar configuração na B3
                                            </button>
                                        </div>
                                    )}

                                    {editing.b3Config && (() => {
                                        const config = editing.b3Config;
                                        const instrument = editing.operationStructure?.instrumentType;
                                        const allowedTypes = instrument ? INSTRUMENT_TO_REGISTRATION_TYPES[instrument] : null;
                                        const instrumentLabel = INSTRUMENT_TYPES.find(i => i.value === instrument)?.label;
                                        const incompatible = allowedTypes ? !allowedTypes.includes(config.b3Product) : false;
                                        return (
                                            <div className="border border-blue-200 dark:border-blue-800 rounded-lg p-4 space-y-3 bg-blue-50/40 dark:bg-blue-900/10">
                                                <div className="flex items-center justify-between">
                                                    <h3 className="text-sm font-semibold">Configuração de registro B3</h3>
                                                    <button onClick={removeB3Config}
                                                        className="flex items-center gap-1 px-3 py-1 text-xs rounded bg-red-100 text-red-700 hover:bg-red-200">
                                                        <MdDelete size={14} /> Remover (não registra)
                                                    </button>
                                                </div>

                                                {incompatible && (
                                                    <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/10 p-2.5 text-xs text-amber-700 dark:text-amber-300">
                                                        Instrumento <b>{instrumentLabel}</b> não é compatível com o fluxo
                                                        B3 <b>{REGISTRATION_TYPES.find(r => r.value === config.b3Product)?.label}</b>.
                                                        A coerência instrumento→fluxo é invariante do product_catalog.
                                                    </div>
                                                )}

                                                <SelectTextField label="Produto B3 (fluxo de registro)"
                                                    items={REGISTRATION_TYPES}
                                                    value={config.b3Product}
                                                    onChange={e => updateB3Config({ b3Product: e.target.value as RegistrationType })} />

                                                <div className="text-[11px] font-semibold text-gray-400 uppercase pt-1">Garantia (Cat.3 — regra do produto)</div>
                                                <div className="grid grid-cols-2 gap-3">
                                                    <SelectTextField label="Registra garantia na B3?"
                                                        items={[{ value: 'yes', label: 'Sim — registra garantia' }, { value: 'no', label: 'Não — registra como Sem Garantia' }]}
                                                        value={config.registersGuarantee ? 'yes' : 'no'}
                                                        onChange={e => updateB3Config({ registersGuarantee: e.target.value === 'yes', collateralTypeCode: e.target.value === 'yes' ? (config.collateralTypeCode && config.collateralTypeCode !== '1' ? config.collateralTypeCode : '2') : '1' })} />
                                                    {config.registersGuarantee && (
                                                        <SelectTextField label="Tipo de garantia B3 (collateralTypeCode)"
                                                            items={GUARANTEE_TYPES.filter(g => g.b3Code !== null && g.b3Code !== '1').map(g => ({ value: g.b3Code as string, label: `${g.b3Code} — ${g.label}` }))}
                                                            value={config.collateralTypeCode || ''}
                                                            emptyLabel="Selecione..."
                                                            onChange={e => updateB3Config({ collateralTypeCode: e.target.value })} />
                                                    )}
                                                </div>

                                                <div className="text-[11px] font-semibold text-gray-400 uppercase pt-1">Classificação por produto (Cat.3)</div>
                                                <div className="grid grid-cols-2 gap-3">
                                                    <TextEdit label="Modalidade operacional SCR (operationModalityCode)" value={config.operationModalityCode || ''}
                                                        onChange={e => updateB3Config({ operationModalityCode: e.target.value })} />
                                                    <TextEdit label="Finalidade SCR (finalityCode)" value={config.finalityCode || ''}
                                                        onChange={e => updateB3Config({ finalityCode: e.target.value })} />
                                                    <SelectTextField label="Tipo de lastro (ballastTypeCode)"
                                                        items={[{ value: 'LCA', label: 'LCA' }, { value: 'CDCA', label: 'CDCA' }]}
                                                        value={config.ballastTypeCode || ''} emptyLabel="Selecione..."
                                                        onChange={e => updateB3Config({ ballastTypeCode: e.target.value })} />
                                                </div>

                                                <p className="text-[11px] text-gray-400 italic pt-1">
                                                    Contas OTC e credor da Agrolend são fixos (não variam por produto) —
                                                    ficam no SSM, fora desta tela. Ver ADR-004.
                                                </p>
                                            </div>
                                        );
                                    })()}
                                </div>
                            )}

                            {section === 'DIMENSA' && (
                                <div className="space-y-4">
                                    <p className="text-sm text-gray-500">
                                        Perfil de registro contábil reutilizável no ERP/Dimensa. Substitui o hardcode
                                        atual do settlement (siglaModalidade, códigos institucionais). Campos são
                                        placeholders a validar contra o serviço erp — ver ponto aberto no
                                        business-context.md.
                                    </p>

                                    {dimensaMode === 'SELECT' && (
                                        <>
                                            <SelectTextField label="Perfil de registro Dimensa"
                                                items={dimensaProfiles.map(p => ({ value: p.profileId, label: p.profileName }))}
                                                value={editing.dimensaRegistrationProfileId || ''}
                                                emptyLabel="Nenhum (produto não registra na Dimensa)"
                                                onChange={e => updateEditing({ dimensaRegistrationProfileId: e.target.value || null })} />

                                            <div className="flex gap-2">
                                                <button onClick={startDimensaEdit} disabled={!editing.dimensaRegistrationProfileId}
                                                    className="flex items-center gap-1 px-3 py-1.5 text-xs rounded bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed">
                                                    <MdEditNote size={14} /> Editar perfil selecionado
                                                </button>
                                                <button onClick={startDimensaNew}
                                                    className="flex items-center gap-1 px-3 py-1.5 text-xs rounded bg-blue-600 text-white hover:bg-blue-700">
                                                    <MdAdd size={14} /> Adicionar novo perfil
                                                </button>
                                            </div>

                                            {editing.dimensaRegistrationProfileId && (() => {
                                                const profile = dimensaProfiles.find(p => p.profileId === editing.dimensaRegistrationProfileId);
                                                if (!profile) return null;
                                                const usedByCount = products.filter(p => p.dimensaRegistrationProfileId === profile.profileId).length;
                                                return (
                                                    <div className="bg-gray-50 dark:bg-slate-700 rounded-lg p-3 text-sm space-y-1">
                                                        <div className="text-xs text-amber-600 mb-2">
                                                            Usado por {usedByCount} produto(s). Editar altera todos.
                                                        </div>
                                                        <div><span className="text-gray-500">Fluxo: </span>{DIMENSA_REGISTRATION_TYPES.find(r => r.value === profile.registrationType)?.label}</div>
                                                        {profile.siglaModalidade !== undefined && <div><span className="text-gray-500">Sigla modalidade: </span>{profile.siglaModalidade || '—'}</div>}
                                                        {profile.codigoCarteira !== undefined && <div><span className="text-gray-500">Código carteira: </span>{profile.codigoCarteira || '—'}</div>}
                                                        {profile.codigoProdutoDimensa !== undefined && <div><span className="text-gray-500">Código produto Dimensa: </span>{profile.codigoProdutoDimensa || '—'}</div>}
                                                        {profile.tipoOperacao !== undefined && <div><span className="text-gray-500">Tipo de operação: </span>{profile.tipoOperacao || '—'}</div>}
                                                    </div>
                                                );
                                            })()}
                                        </>
                                    )}

                                    {(dimensaMode === 'EDIT' || dimensaMode === 'NEW') && dimensaDraft && (
                                        <div className="border border-blue-200 dark:border-blue-800 rounded-lg p-4 space-y-3 bg-blue-50/40 dark:bg-blue-900/10">
                                            <div className="flex items-center justify-between">
                                                <h3 className="text-sm font-semibold">
                                                    {dimensaMode === 'NEW' ? 'Novo perfil Dimensa' : 'Editar perfil Dimensa'}
                                                </h3>
                                                <div className="flex gap-2">
                                                    <button onClick={cancelDimensaEdit} className="px-3 py-1 text-xs rounded bg-gray-200 text-gray-700 hover:bg-gray-300">Cancelar</button>
                                                    <button onClick={saveDimensaDraft} className="px-3 py-1 text-xs rounded bg-blue-600 text-white hover:bg-blue-700">Salvar perfil</button>
                                                </div>
                                            </div>

                                            <TextEdit label="Nome do perfil *" value={dimensaDraft.profileName}
                                                onChange={e => updateDimensaDraft({ profileName: e.target.value })} />

                                            <SelectTextField label="Fluxo de registro"
                                                items={DIMENSA_REGISTRATION_TYPES}
                                                value={dimensaDraft.registrationType}
                                                onChange={e => updateDimensaDraft({ registrationType: e.target.value as DimensaRegistrationType })} />

                                            {dimensaDraft.registrationType === 'DIMENSA' && (
                                                <>
                                                    <TextEdit label="Sigla modalidade (ex.: EM03F365, DESSCOOB)" value={dimensaDraft.siglaModalidade || ''}
                                                        onChange={e => updateDimensaDraft({ siglaModalidade: e.target.value })} />
                                                    <TextEdit label="Código carteira" value={dimensaDraft.codigoCarteira || ''}
                                                        onChange={e => updateDimensaDraft({ codigoCarteira: e.target.value })} />
                                                    <TextEdit label="Código produto Dimensa" value={dimensaDraft.codigoProdutoDimensa || ''}
                                                        onChange={e => updateDimensaDraft({ codigoProdutoDimensa: e.target.value })} />
                                                    <TextEdit label="Tipo de operação" value={dimensaDraft.tipoOperacao || ''}
                                                        onChange={e => updateDimensaDraft({ tipoOperacao: e.target.value })} />
                                                </>
                                            )}

                                            {dimensaDraft.registrationType === 'NONE' && (
                                                <p className="text-xs text-gray-500 italic">
                                                    Produto não registra na Dimensa — nenhum parâmetro adicional necessário.
                                                </p>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                ) : selectedProduct ? (
                    <div className="h-full p-6 space-y-4 overflow-y-auto">
                        <div className="flex items-center justify-between">
                            <h2 className="text-lg font-semibold flex items-center gap-2">
                                {selectedProduct.icon && <span className="text-2xl">{selectedProduct.icon}</span>}
                                {selectedProduct.commercialName}
                            </h2>
                            <button onClick={() => openEdit(selectedProduct)} className="text-xs text-blue-600 hover:underline flex items-center gap-1">
                                <MdEditNote size={16} /> Editar
                            </button>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-300">{selectedProduct.description}</p>

                        <div className="grid grid-cols-2 gap-3 text-sm">
                            <div><label className="text-xs text-gray-500 uppercase">Ativo resultante</label><p>{ASSET_TYPE_OPTIONS.find(a => a.value === selectedProduct.resultingAssetType)?.label}</p></div>
                            <div><label className="text-xs text-gray-500 uppercase">Status</label><p><BadgeStatus color={statusColor(selectedProduct.status)} text={statusLabel(selectedProduct.status)} /></p></div>
                            <div><label className="text-xs text-gray-500 uppercase">Taxa</label><p>{selectedProduct.minRate}% — {selectedProduct.maxRate}% a.m.</p></div>
                            <div><label className="text-xs text-gray-500 uppercase">Valor máximo</label><p>{formatCurrency(selectedProduct.maxAmount, selectedProduct.currency)}</p></div>
                        </div>

                        {selectedProduct.operationStructure && (
                            <div className="border-t pt-3">
                                <label className="text-xs text-gray-500 uppercase font-medium">Estrutura da operação</label>
                                <div className="mt-1 space-y-1 text-sm">
                                    <div className="flex justify-between"><span className="text-gray-500">Instrumento</span><span className="font-medium text-right">{INSTRUMENT_TYPES.find(i => i.value === selectedProduct.operationStructure!.instrumentType)?.label}</span></div>
                                    <div className="flex justify-between"><span className="text-gray-500">Recebível descontado</span><span>{DISCOUNTED_RECEIVABLE_TYPES.find(d => d.value === selectedProduct.operationStructure!.discountedReceivable)?.label}</span></div>
                                    <div className="flex justify-between"><span className="text-gray-500">Regresso do recebível</span><span>{TERNARY_OPTIONS.find(t => t.value === selectedProduct.operationStructure!.receivableRecourse)?.label}</span></div>
                                    <div className="flex justify-between"><span className="text-gray-500">Garantia obrigatória</span><span className="text-right">{selectedProduct.operationStructure!.mandatoryGuaranteeLabel}</span></div>
                                    <div className="flex justify-between"><span className="text-gray-500">Regresso da garantia</span><span>{TERNARY_OPTIONS.find(t => t.value === selectedProduct.operationStructure!.guaranteeRecourse)?.label}</span></div>
                                    <div className="flex justify-between"><span className="text-gray-500">Fatura</span><span className="text-right">{INVOICE_GRANULARITY_OPTIONS.find(g => g.value === selectedProduct.operationStructure!.invoiceGranularity)?.label}</span></div>
                                    <div className="flex justify-between"><span className="text-gray-500">Lastro para LCA</span><span>{selectedProduct.operationStructure!.isLcaBallast ? 'Sim' : 'Não'}</span></div>
                                </div>
                            </div>
                        )}

                        <div className="border-t pt-3">
                            <label className="text-xs text-gray-500 uppercase font-medium">Elegibilidade</label>
                            {selectedProduct.eligibilityCriteria.length === 0 ? (
                                <p className="text-sm text-gray-400 italic mt-1">Sem critérios</p>
                            ) : (
                                <div className="mt-1 space-y-1">
                                    {selectedProduct.eligibilityCriteria.map(c => (
                                        <div key={c.criteriaId} className="text-sm flex justify-between">
                                            <span>{getParticipantTypeLabel(c.participantTypeId)}</span>
                                            <span className="font-medium">{getRatingRangeLabel(c.minRating, c.maxRating)}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="border-t pt-3">
                            <label className="text-xs text-gray-500 uppercase font-medium">Garantias</label>
                            {selectedProduct.guarantees.length === 0 ? (
                                <p className="text-sm text-gray-400 italic mt-1">Nenhuma</p>
                            ) : (
                                <div className="mt-1 space-y-1">
                                    {selectedProduct.guarantees.map((g, i) => (
                                        <div key={i} className="flex items-center gap-2 text-sm">
                                            <BadgeStatus color={g.requirement === 'MANDATORY' ? 'red' : 'blue'} text={g.requirement === 'MANDATORY' ? 'Obrigatória' : 'Opcional'} />
                                            <span>{getGuaranteeTypeLabel(g.type)}{g.retentionPercentage ? ` (${g.retentionPercentage}%)` : ''}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="border-t pt-3">
                            <label className="text-xs text-gray-500 uppercase font-medium">Participantes</label>
                            {selectedProduct.allowedParticipants.length === 0 ? (
                                <p className="text-sm text-gray-400 italic mt-1">Nenhum configurado</p>
                            ) : (
                                <div className="mt-1 space-y-2">
                                    {selectedProduct.allowedParticipants.map(p => (
                                        <div key={p.allowedParticipantId} className="text-sm">
                                            <div className="font-medium">{getParticipantTypeLabel(p.participantTypeId)} {p.isRequired && <span className="text-red-500 text-xs">obrigatório</span>}</div>
                                            <div className="text-xs text-gray-500">{p.requiredDocuments.map(d => REQUIRED_DOCUMENT_TYPES.find(r => r.value === d.documentType)?.label).join(', ') || 'Sem documento exigido'}</div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="border-t pt-3">
                            <label className="text-xs text-gray-500 uppercase font-medium">Instrumentos por estágio</label>
                            {selectedProduct.instrumentBindings.length === 0 ? (
                                <p className="text-sm text-gray-400 italic mt-1">Nenhum</p>
                            ) : (
                                <div className="mt-1 space-y-1">
                                    {selectedProduct.instrumentBindings.map(t => (
                                        <div key={t.bindingId} className="text-sm flex justify-between">
                                            <span>{DEAL_STAGES.find(s => s.value === t.stage)?.label}{t.customerId ? ` · cliente ${t.customerId}` : ''}</span>
                                            <span className="font-medium text-right">{getInstrumentName(t.instrumentCode) || '—'}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="border-t pt-3">
                            <label className="text-xs text-gray-500 uppercase font-medium">Registro B3</label>
                            <p className="text-sm mt-1">
                                {selectedProduct.b3Config
                                    ? `${REGISTRATION_TYPES.find(r => r.value === selectedProduct.b3Config!.b3Product)?.label || selectedProduct.b3Config.b3Product}${selectedProduct.b3Config.registersGuarantee ? ` · garantia ${GUARANTEE_TYPES.find(g => g.b3Code === selectedProduct.b3Config!.collateralTypeCode)?.label || selectedProduct.b3Config.collateralTypeCode}` : ' · sem garantia'}`
                                    : 'Não registra na B3'}
                            </p>
                        </div>

                        <div className="border-t pt-3">
                            <label className="text-xs text-gray-500 uppercase font-medium">Registro ERP-Dimensa</label>
                            <p className="text-sm mt-1">{getDimensaProfileName(selectedProduct.dimensaRegistrationProfileId)}</p>
                        </div>
                    </div>
                ) : null}
            >
                <div className="flex flex-col h-full bg-white dark:bg-slate-800 relative overflow-hidden">
                    <div className="flex-shrink-0 border-b border-gray-200 dark:border-slate-700 p-3 bg-white dark:bg-slate-800 z-10">
                        <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Catálogo de Produtos</h1>
                        <p className="text-xs text-gray-500 mt-1">
                            Prototipação — dados mock local, nenhuma API real ainda (ver product_catalog/docs/context/business-context.md)
                        </p>
                    </div>
                    <div className="flex-1 overflow-y-auto p-4">
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                            {filteredProducts.map(product => (
                                <button
                                    key={product.productId}
                                    onClick={() => { setSelectedProduct(product); setEditing(null); }}
                                    className="group relative flex flex-col text-left bg-white dark:bg-slate-900 border-2 border-emerald-600/60 hover:border-emerald-600 rounded-2xl p-4 shadow-sm hover:shadow-lg transition-all min-h-[190px]"
                                >
                                    {product.isNew && (
                                        <span className="absolute -top-2 -right-2 text-[10px] font-bold bg-red-500 text-white px-2 py-0.5 rounded-full shadow">NEW</span>
                                    )}

                                    <div className="flex items-start justify-between mb-2">
                                        <div className="w-12 h-12 rounded-xl bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center text-2xl">
                                            {product.icon ? <span>{product.icon}</span> : <ProductIcon assetType={product.resultingAssetType} />}
                                        </div>
                                        <BadgeStatus color={statusColor(product.status)} text={statusLabel(product.status)} />
                                    </div>

                                    <div className="font-bold text-emerald-700 dark:text-emerald-400 text-lg leading-tight">
                                        {product.commercialName}
                                    </div>
                                    {product.fullName && (
                                        <div className="text-[11px] text-gray-400 italic mb-1 line-clamp-1">{product.fullName}</div>
                                    )}

                                    <div className="flex items-center gap-1.5 mt-1 mb-2 flex-wrap">
                                        <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-300">
                                            {ASSET_TYPE_OPTIONS.find(a => a.value === product.resultingAssetType)?.label}
                                        </span>
                                        <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-300">
                                            {product.currency}
                                        </span>
                                    </div>

                                    <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-3 flex-1">
                                        {product.description}
                                    </p>

                                    <div className="mt-2 pt-2 border-t border-gray-100 dark:border-slate-700 flex items-center justify-between text-[11px] text-gray-500">
                                        <span>{product.minRate}% — {product.maxRate}% a.m.</span>
                                        <span className="font-medium">{formatCurrency(product.maxAmount, product.currency)}</span>
                                    </div>
                                </button>
                            ))}
                        </div>
                        {filteredProducts.length === 0 && (
                            <div className="flex items-center justify-center py-16">
                                <p className="text-gray-400">Nenhum produto encontrado com os filtros atuais.</p>
                            </div>
                        )}
                    </div>
                </div>
            </ThreeColumnLayout>
        </>
    );
};

export default ProductCatalogPageForm;
